// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A form component for managing asset accounting properties.
 * This module provides a comprehensive interface for updating financial
 * tracking information including cost basis, useful life, salvage value,
 * and in-service date. Integrates with react-hook-form for validation
 * and state management.
 *
 * Key features:
 * - Real-time validation and formatting
 * - Partial updates support
 * - Currency and numeric input handling
 * - Date normalization
 * - Informative tooltips
 * - Optimistic updates
 * - Responsive grid layout
 * - Automatic state persistence
 */
import { CostBreakdownFields } from '@/accounting/components/cost-breakdown-fields';
import {
  AccountingCostFormValues,
  costBreakdownParameters,
  toAccountingCostFormValues,
} from '@/accounting/utilities/cost-breakdown-form';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { DetailedAsset } from '@/asset/api/types';
import {
  UpdateAssetAccountingPropertiesParameters,
  updateAssetAccountingProperties,
} from '@/asset/api/update-asset-accounting-properties';
import { updateInServiceDateManagement } from '@/asset/api/update-in-service-date-management';
import { FormCurrencySelect } from '@/components/form-currency-select';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  MAX_USEFUL_LIFE_MONTHS,
  MIN_USEFUL_LIFE_MONTHS,
} from '@/constants/accounting-form-fields';
import {
  OrgDefaultCurrencyCode,
  orgDefaultCurrencyCodeSchema,
} from '@/constants/currency';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import {
  AutoAwesomeOutlined,
  InfoOutlined,
} from '@/constants/hardfin-standard-icons';
import { clearableCurrencyInputSchema } from '@/currency/api/types';
import { padAmountToMinorUnit } from '@/currency/utilities/serialize';
import { toCurrencyInputValueFromString } from '@/currency/utilities/to-form-value';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  BoxProps,
  Button,
  CircularProgress,
  Collapse,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating accounting form data.
 * All fields are optional and can be null to support partial updates.
 * This allows users to update only specific fields while leaving
 * others unchanged.
 *
 * Fields:
 * - costBasis: Purchase price and setup costs
 * - usefulLife: Expected lifespan in months
 * - salvageValue: Expected end-of-life value
 * - inServiceDate: When depreciation begins (must be on or after January 1, 1970)
 */
// Errors this form surfaces on a specific field, so the generic snackbar stays quiet.
const FIELD_REPORTED_ERROR_CODES: number[] = [
  ERROR_CODE_MAPPING.accounting_invalid_useful_life,
  ERROR_CODE_MAPPING.asset_mixed_currency,
];

const formSchema = z.object({
  usefulLife: z
    .string()
    .nullable()
    .refine(
      (value) => {
        if (value === null || value === '') return true;
        const num = Number(value);
        return (
          !isNaN(num) &&
          num >= MIN_USEFUL_LIFE_MONTHS &&
          num <= MAX_USEFUL_LIFE_MONTHS
        );
      },
      {
        message: `Useful life must be between ${MIN_USEFUL_LIFE_MONTHS} and ${MAX_USEFUL_LIFE_MONTHS} months`,
      }
    ),
  salvageValue: clearableCurrencyInputSchema,
  inServiceDate: dateSchema.nullable().refine(
    (value) => {
      if (value === null) return true;
      return value >= MINIMUM_DATE;
    },
    {
      message: 'In-service date cannot be before January 1, 1970',
    }
  ),
  simpleCostBasis: clearableCurrencyInputSchema,
  billOfMaterials: clearableCurrencyInputSchema,
  directLabor: clearableCurrencyInputSchema,
  allocatedIndirect: clearableCurrencyInputSchema,
  freightInbound: clearableCurrencyInputSchema,
  freightOutbound: clearableCurrencyInputSchema,
  installation: clearableCurrencyInputSchema,
  tax: clearableCurrencyInputSchema,
  tariffs: clearableCurrencyInputSchema,
  interest: clearableCurrencyInputSchema,
  // Only the app-allowed currencies are offered and only they are accepted, so the
  // form validates against the same set rather than the recognized registry.
  costCurrencyCode: orgDefaultCurrencyCodeSchema,
});

// The form holds money fields as their input shape ({ amount, isoCode });
// FormOutput is the post-resolver shape where each amount is a Currency or null.
interface FormData extends AccountingCostFormValues {
  inServiceDate: DateTime | null;
  costCurrencyCode: OrgDefaultCurrencyCode;
}

type FormOutput = z.output<typeof formSchema>;

/** Seeds the form from a mutation response so it shows the saved values immediately. */
function toFormValues(accounting: DetailedAsset['accounting']): FormData {
  const costCurrencyCode = accounting.costCurrencyCode;
  return {
    ...toAccountingCostFormValues(accounting, costCurrencyCode),
    inServiceDate: accounting.inServiceDate ?? null,
    costCurrencyCode,
  };
}

/**
 * Props for configuring the AssetAccountingForm component.
 * @typedef {Object} Props
 */
interface Props extends Pick<BoxProps, 'sx'> {
  /**
   * Asset to update accounting properties for.
   * Only requires key and accounting properties for operation.
   * Other asset properties are not used by this component.
   */
  asset: DetailedAsset;
  /**
   * Route-owned sections (depreciation, cost adjustments, useful-life
   * revisions) rendered after the form, inside the accounting card.
   */
  footer?: ReactNode;
}

/**
 * A form component for managing asset accounting properties.
 * Provides a user interface for updating financial tracking information
 * with validation, tooltips for guidance, and automatic state management.
 *
 * @param props - Component configuration options
 */
export function AssetAccountingForm(props: Props) {
  const queryClient = useQueryClient();

  // Track when user manually toggles to advanced mode to prevent flickering
  const [userToggledToAdvanced, setUserToggledToAdvanced] = useState(false);

  // Track toggle count to force UI component remount when switching modes
  const [toggleCount, setToggleCount] = useState(0);

  const [isUpdatingInServiceManagement, setIsUpdatingInServiceManagement] =
    useState(false);
  const [targetAutomaticState, setTargetAutomaticState] = useState<
    boolean | null
  >(null);
  const MIN_UPDATE_IN_SERVICE_MANAGEMENT_TIME_MS = 500;

  const updateInServiceManagementMutation = useMutation({
    mutationFn: (automatic: boolean) => {
      return updateInServiceDateManagement(props.asset.key, automatic);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.asset.key] });

      const resetData = toFormValues(data.asset.accounting);

      setTimeout(() => {
        setIsUpdatingInServiceManagement(false);
        setTargetAutomaticState(null);
        reset(resetData);
      }, MIN_UPDATE_IN_SERVICE_MANAGEMENT_TIME_MS);
    },
    onError: () => {
      setTimeout(() => {
        setIsUpdatingInServiceManagement(false);
        setTargetAutomaticState(null);
      }, MIN_UPDATE_IN_SERVICE_MANAGEMENT_TIME_MS);
    },
  });

  const updateAccountingPropertiesMutation = useMutation({
    mutationFn: (parameters: UpdateAssetAccountingPropertiesParameters) => {
      return updateAssetAccountingProperties(props.asset.key, parameters);
    },
    onSuccess: (data) => {
      // Update the query cache directly with the response data
      // This prevents a refetch and eliminates the race condition
      // The cache stores just the asset data (not wrapped in { asset: data })
      queryClient.setQueryData(['asset', props.asset.key], data.asset);

      reset(toFormValues(data.asset.accounting));
    },
    onError: (error) => {
      const apiError = handleAPIError(error);

      // Check for invalid useful life
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.accounting_invalid_useful_life
      ) {
        setError('usefulLife', {
          type: 'custom',
          message: `Useful life must be between ${MIN_USEFUL_LIFE_MONTHS} and ${MAX_USEFUL_LIFE_MONTHS} months`,
        });
      }

      // The cost currency locks once historical cost data is recorded, which can happen
      // between page load and submit. Say so on the field rather than in the snackbar.
      if (apiError.apiStatusCode === ERROR_CODE_MAPPING.asset_mixed_currency) {
        setError('costCurrencyCode', {
          type: 'custom',
          message:
            'The cost currency for this asset is locked and can no longer be changed.',
        });
      }
    },
  });

  const defaultValues = useMemo<FormData>(() => {
    const accountingCurrencyCode = props.asset.accounting.costCurrencyCode;

    // Check if we have any breakdown data
    const breakdownValues = [
      props.asset.accounting.billOfMaterials,
      props.asset.accounting.directLabor,
      props.asset.accounting.allocatedIndirect,
      props.asset.accounting.freightInbound,
      props.asset.accounting.freightOutbound,
      props.asset.accounting.installation,
      props.asset.accounting.tax,
      props.asset.accounting.tariffs,
      props.asset.accounting.interest,
    ];

    const hasBreakdownData = breakdownValues.some((field) => field !== null);

    const costFormValues = toAccountingCostFormValues(
      props.asset.accounting,
      accountingCurrencyCode
    );

    // If we have breakdown data, calculate the correct initial simpleCostBasis
    if (hasBreakdownData) {
      const sum = breakdownValues.reduce((total, value) => {
        if (value === null) return total;
        return total.plus(value.amount);
      }, new Decimal(0));

      costFormValues.simpleCostBasis = toCurrencyInputValueFromString(
        sum.gt(0) ? sum.toFixed(2) : null,
        accountingCurrencyCode
      );
    }

    return {
      ...costFormValues,
      inServiceDate: props.asset.accounting.inServiceDate ?? null,
      costCurrencyCode: accountingCurrencyCode,
    };
  }, [props.asset.accounting]);

  const methods = useForm<FormData, unknown, FormOutput>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { control, formState, reset, setError, handleSubmit, watch, setValue } =
    methods;

  const { dirtyFields, isDirty } = formState;

  const breakdownAmounts = watch([
    'billOfMaterials.amount',
    'directLabor.amount',
    'allocatedIndirect.amount',
    'freightInbound.amount',
    'freightOutbound.amount',
    'installation.amount',
    'tax.amount',
    'tariffs.amount',
    'interest.amount',
  ]);

  // Derive advanced mode state from user intent OR current form data
  const showAdvancedBreakdown =
    userToggledToAdvanced ||
    breakdownAmounts.some((field) => field !== null && field !== '');

  // The single accounting currency drives every money field's symbol; the header
  // selector is the only control and locks once the asset's currency is locked.
  const costCurrencyCode = watch('costCurrencyCode');

  const calculatedSum = useMemo(() => {
    const allFieldsNull = breakdownAmounts.every(
      (field) => field === null || field === ''
    );

    if (allFieldsNull) return null;

    const sum = breakdownAmounts.reduce((total, value) => {
      const amount = value ? parseCurrencyAmount(value) : null;
      return amount === null ? total : total.plus(amount);
    }, new Decimal(0));

    return padAmountToMinorUnit(sum, costCurrencyCode);
  }, [breakdownAmounts, costCurrencyCode]);

  useEffect(() => {
    if (showAdvancedBreakdown) {
      setValue('simpleCostBasis.amount', calculatedSum ?? '', {
        shouldDirty: false,
        shouldValidate: false,
        shouldTouch: false,
      });
    }
  }, [calculatedSum, showAdvancedBreakdown, setValue]);

  // Reset form when backend data changes (e.g., from movement updates triggering in-service date recalculation)
  // Only reset if the form is not dirty to avoid overwriting active user edits
  // Skip reset during the in-service management update timeout to avoid resetting while loading indicator is still showing
  // No race condition with accounting updates since we update the cache directly instead of invalidating
  useEffect(() => {
    if (!isDirty && !isUpdatingInServiceManagement) {
      reset(defaultValues);
    }
  }, [defaultValues, isDirty, isUpdatingInServiceManagement, reset]);

  /**
   * Handles form submission after validation.
   * Only includes changed fields in the update request and
   * handles special formatting for date fields.
   */
  const handleValidSubmission: SubmitHandler<FormOutput> = (data) => {
    const parameters: UpdateAssetAccountingPropertiesParameters =
      costBreakdownParameters(data, dirtyFields, showAdvancedBreakdown);

    if (dirtyFields.costCurrencyCode) {
      parameters.costCurrencyCode = data.costCurrencyCode;
    }

    if (dirtyFields.usefulLife) {
      parameters.usefulLife =
        data.usefulLife === null ? null : Number(data.usefulLife);
    }

    if (dirtyFields.inServiceDate) {
      parameters.inServiceDate = data.inServiceDate;
    }

    if (isDirty) {
      updateAccountingPropertiesMutation.mutate(parameters);
    }
  };

  /**
   * Resets the form to its initial values.
   * Discards all unsaved changes.
   */
  const handleClickReset = () => {
    // Reset to the original values from defaultValues (which includes correct initial calculations)
    reset(defaultValues);

    // Reset UI state - determine if advanced breakdown should be shown based on original data
    const originalBreakdownValues = [
      props.asset.accounting.billOfMaterials,
      props.asset.accounting.directLabor,
      props.asset.accounting.allocatedIndirect,
      props.asset.accounting.freightInbound,
      props.asset.accounting.freightOutbound,
      props.asset.accounting.installation,
      props.asset.accounting.tax,
      props.asset.accounting.tariffs,
      props.asset.accounting.interest,
    ];

    const originalHasBreakdownData = originalBreakdownValues.some(
      (field) => field !== null
    );

    // Reset user toggle state to match original data state
    setUserToggledToAdvanced(originalHasBreakdownData);

    // Force a re-render by incrementing toggle count to ensure UI reflects the reset
    setToggleCount((prev) => prev + 1);
  };

  const handleToggleAdvanced = () => {
    // Increment toggle count to force UI component remount
    setToggleCount((prev) => prev + 1);

    if (!showAdvancedBreakdown) {
      // Switching FROM simple TO advanced
      // Set user intent to stay in advanced mode
      setUserToggledToAdvanced(true);

      // Put current cost basis into bill of materials
      const currentCostBasis = watch('simpleCostBasis.amount');
      setValue('billOfMaterials.amount', currentCostBasis, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
      setValue('directLabor.amount', '');
      setValue('allocatedIndirect.amount', '');
      setValue('freightInbound.amount', '');
      setValue('freightOutbound.amount', '');
      setValue('installation.amount', '');
      setValue('tax.amount', '');
      setValue('tariffs.amount', '');
      setValue('interest.amount', '');
    } else {
      // Switching FROM advanced TO simple
      // Clear user intent to stay in advanced mode
      setUserToggledToAdvanced(false);

      // Set simple cost basis to the calculated sum, clear all breakdown fields
      setValue('simpleCostBasis.amount', calculatedSum ?? '', {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });

      // Clear all breakdown fields (these should also dirty the form)
      setValue('billOfMaterials.amount', '', { shouldDirty: true });
      setValue('directLabor.amount', '', { shouldDirty: true });
      setValue('allocatedIndirect.amount', '', { shouldDirty: true });
      setValue('freightInbound.amount', '', { shouldDirty: true });
      setValue('freightOutbound.amount', '', { shouldDirty: true });
      setValue('installation.amount', '', { shouldDirty: true });
      setValue('tax.amount', '', { shouldDirty: true });
      setValue('tariffs.amount', '', { shouldDirty: true });
      setValue('interest.amount', '', { shouldDirty: true });
    }
  };

  /**
   * Handles toggling the in-service date management mode.
   * Sets loading state and stores the target state to prevent message flickering
   * when the API response updates the asset prop before the timeout completes.
   *
   * @param automatic - Whether to use automatic (AI) or manual management
   */
  const handleUpdateInServiceManagement = (automatic: boolean) => {
    setIsUpdatingInServiceManagement(true);
    setTargetAutomaticState(automatic);
    updateInServiceManagementMutation.mutate(automatic);
  };

  return (
    <FormProvider {...methods}>
      <HardfinTabCardWrapper
        title="Accounting"
        actions={
          // Padding sits on the row; Stack resets its direct children's margins.
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mr: 2, px: 1, py: 1 }}
          >
            <FormCurrencySelect
              name="costCurrencyCode"
              control={control}
              label="Accounting currency"
              disabled={props.asset.accounting.isCostCurrencyLocked}
              disabledTooltip="This asset's cost currency is locked because historical cost data has already been recorded"
              sx={{ minWidth: 220 }}
            />
            <Tooltip
              arrow
              placement="top"
              title="Accounting currency determines how costs are captured for this asset during its life. (This controls the currency used to log costs for the asset, it does not convert foreign currencies.)"
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
        }
      >
        <Box
          component="form"
          onSubmit={handleSubmit(handleValidSubmission)}
          sx={props.sx}
        >
          <SupportSnackbar
            isOpen={
              updateAccountingPropertiesMutation.isError &&
              updateAccountingPropertiesMutation.error !== null &&
              // Errors reported on a field speak for themselves; the generic snackbar
              // would only bury them.
              !FIELD_REPORTED_ERROR_CODES.includes(
                handleAPIError(updateAccountingPropertiesMutation.error)
                  .apiStatusCode
              )
            }
            onClose={updateAccountingPropertiesMutation.reset}
            message="An error occurred while updating accounting details."
          />
          <SupportSnackbar
            isOpen={updateInServiceManagementMutation.isError}
            onClose={updateInServiceManagementMutation.reset}
            message="An error occurred while updating in-service date management."
          />
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr"
            columnGap={6}
            rowGap={3}
            alignItems="flex-start"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormCurrencyTextField
                key={`simpleCostBasis-${toggleCount}`}
                fullWidth
                label="Cost basis"
                name="simpleCostBasis"
                control={control}
                lockTo={costCurrencyCode}
                compact
                disabled={showAdvancedBreakdown}
              />
              <Tooltip
                arrow
                placement="top"
                title="Fully loaded cost basis of this asset at the time it was put into service"
              >
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormNumberTextField
                fullWidth
                label="Useful life (months)"
                name="usefulLife"
                control={control}
                allowNegative={false}
                maximumFractionDigits={0}
              />
              <Tooltip
                arrow
                placement="top"
                title={`Useful life in months of an asset for accounting purposes (must be between ${MIN_USEFUL_LIFE_MONTHS} and ${MAX_USEFUL_LIFE_MONTHS} months)`}
              >
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormCurrencyTextField
                fullWidth
                label="Salvage value"
                name="salvageValue"
                control={control}
                lockTo={costCurrencyCode}
                compact
              />
              <Tooltip
                arrow
                placement="top"
                title="Salvage value is the amount an asset is expected to be worth after its useful life"
              >
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Stack
                direction="column"
                justifyContent="flex-start"
                spacing={1}
                width="100%"
              >
                <FormDatePicker
                  label="In-service date"
                  name="inServiceDate"
                  control={control}
                />
                <Stack direction="row" spacing={1}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    px={2}
                    width="100%"
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {isUpdatingInServiceManagement ? (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            {targetAutomaticState
                              ? 'Computing...'
                              : 'Configuring...'}
                          </Typography>
                          <CircularProgress size={16} />
                        </>
                      ) : (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            {props.asset.accounting
                              .isInServiceDateManagedAutomatically
                              ? 'Computed by Hardfin AI'
                              : 'Configured manually'}
                          </Typography>
                          {props.asset.accounting
                            .isInServiceDateManagedAutomatically && (
                            <AutoAwesomeOutlined
                              fontSize="small"
                              color="action"
                              sx={{ fontSize: 18 }}
                            />
                          )}
                        </>
                      )}
                    </Stack>
                    {!isUpdatingInServiceManagement && (
                      <Link
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleUpdateInServiceManagement(
                            !props.asset.accounting
                              .isInServiceDateManagedAutomatically
                          );
                        }}
                        variant="caption"
                        sx={{ '&:hover': { cursor: 'pointer' } }}
                      >
                        <Stack direction="row" spacing={0.5}>
                          <Typography variant="caption">
                            {props.asset.accounting
                              .isInServiceDateManagedAutomatically
                              ? 'Manage manually'
                              : 'Use Hardfin AI'}
                          </Typography>
                          {!props.asset.accounting
                            .isInServiceDateManagedAutomatically && (
                            <AutoAwesomeOutlined
                              fontSize="small"
                              color="primary"
                              sx={{ fontSize: 18 }}
                            />
                          )}
                        </Stack>
                      </Link>
                    )}
                  </Stack>
                </Stack>
              </Stack>
              <Tooltip
                arrow
                placement="top"
                title="The date the asset was placed in service (when depreciation starts)"
              >
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </Stack>
          </Box>

          <Box paddingTop={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Button
                variant={showAdvancedBreakdown ? 'text' : 'outlined'}
                onClick={handleToggleAdvanced}
                size="small"
              >
                {showAdvancedBreakdown ? 'Hide' : 'Show'} advanced cost
                breakdown
              </Button>
              {isDirty && (
                <Stack direction="row" spacing={1}>
                  <Button onClick={handleClickReset}>Cancel</Button>
                  <Button variant="contained" color="secondary" type="submit">
                    Save
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>

          <Collapse in={showAdvancedBreakdown}>
            <CostBreakdownFields
              costCurrencyCode={costCurrencyCode}
              toggleCount={toggleCount}
            />
          </Collapse>
        </Box>
        {props.footer}
      </HardfinTabCardWrapper>
    </FormProvider>
  );
}
